import { ReactNode, useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { decodeFunctionData, Hex, parseAbiItem } from 'viem'

import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { SecondaryDisplayButtons } from '~/components/txn-display/SecondaryDisplayButtons'
import { ContractCallSection } from '~/components/txn-display/calls/ContractCallSection'
import { useRunTenderlySimulation } from '~/components/txn-display/state-change/hooks/useRunTenderlySimulation'
import {
  DecodedCallArg,
  DecodedCallSummary,
  TxnData,
} from '~/components/txn-display/state-change/types'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  ServerGetRequestFunction,
  useServerRequests,
} from '~/hooks/useRequests'
import {
  BackendTransactionCall,
  BackendTransactionDecodedCall,
  IAddress,
} from '~/types'
import { shortenAddress } from '~/utils/addresses'

type ContractCallSectionInput = {
  smartAccountAddress: IAddress
  chainId: number
  calls: BackendTransactionCall[]
  addressesToWatch?: IAddress[]
  source?: string
}

type SignatureLookupResult = {
  signature: string
  functionName: string
}

type SignatureLookupResponse = {
  selector: string
  signature: string | null
  functionName: string | null
}

async function lookupFunctionSignature(
  selector: string,
  serverGetRequest: ServerGetRequestFunction,
): Promise<SignatureLookupResult | undefined> {
  const response = await serverGetRequest<SignatureLookupResponse>({
    path: `/api/public/function-signature/${selector}`,
  })
  if (response?.data?.signature && response?.data?.functionName) {
    return {
      signature: response.data.signature,
      functionName: response.data.functionName,
    }
  }
  // No match found - return undefined (this gets cached, which is fine for "not found")
  return undefined
}

function decodeCallArgs(
  signature: string,
  calldata: string,
): DecodedCallArg[] | undefined {
  try {
    const abiItem = parseAbiItem(`function ${signature}`)
    if (abiItem.type !== 'function') return undefined

    const { args } = decodeFunctionData({
      abi: [abiItem],
      data: calldata as Hex,
    })

    if (!args) return undefined

    return abiItem.inputs.map((input, index) => ({
      name: input.name || `arg${index}`,
      value: args[index],
    }))
  } catch {
    return undefined
  }
}

function getSelector(data: string | undefined): string | undefined {
  if (!data || data.length < 10) return undefined
  return data.slice(0, 10)
}

function buildTitle(
  signatureQueries: { data?: SignatureLookupResult }[],
  calls: BackendTransactionCall[],
  source?: string,
): string | undefined {
  const firstMatchIndex = signatureQueries.findIndex(
    (q) => q.data?.functionName,
  )
  if (firstMatchIndex === -1) return undefined

  const primaryFunctionName =
    signatureQueries[firstMatchIndex].data!.functionName
  const contractAddress = calls[firstMatchIndex]?.to
  const suffix = source ? ` (${source})` : ''

  const totalMatches = signatureQueries.filter(
    (q) => q.data?.functionName,
  ).length

  if (totalMatches === 1) {
    if (contractAddress) {
      return `Call ${primaryFunctionName} on ${shortenAddress(contractAddress)}${suffix}`
    }
    return `Call ${primaryFunctionName}${suffix}`
  }

  return `Call ${primaryFunctionName} + ${totalMatches - 1} more${suffix}`
}

export function useContractCallSection(data: ContractCallSectionInput): {
  contractCallSection: ReactNode
  txnData: TxnData
  title: string | undefined
} {
  const { serverGetRequest } = useServerRequests()

  const txnData = useMemo<TxnData>(
    () => ({
      smartAccountAddress: data.smartAccountAddress,
      chainId: data.chainId,
      calls: data.calls as BackendTransactionDecodedCall[],
      addressesToWatch: data.addressesToWatch ?? [data.smartAccountAddress],
    }),
    [data.smartAccountAddress, data.chainId, data.calls, data.addressesToWatch],
  )

  const selectors = useMemo(
    () => data.calls.map((call) => getSelector(call.data)),
    [data.calls],
  )

  const signatureQueries = useQueries({
    queries: selectors.map((selector) => ({
      queryKey: [QUERY_KEYS.FUNCTION_SIGNATURE, selector],
      queryFn: () => lookupFunctionSignature(selector!, serverGetRequest),
      enabled: !!selector,
      staleTime: Infinity,
      gcTime: Infinity,
    })),
  })

  const decodedCalls = useMemo<DecodedCallSummary[] | undefined>(() => {
    if (!data.calls?.length) return undefined
    return data.calls.map((call, index) => {
      const result = signatureQueries[index]?.data
      const args = result?.signature
        ? decodeCallArgs(result.signature, call.data)
        : undefined

      return {
        id: `${call.to}-${index}`,
        target: call.to,
        value: call.value,
        functionName: result?.functionName,
        args,
        data: call.data as Hex,
      }
    })
  }, [data.calls, signatureQueries])

  const runTenderlySimulation = useRunTenderlySimulation({ txnData })
  const [isShowingCalls, setIsShowingCalls] = useState(false)

  const contractCallItems = useMemo(() => {
    return data.calls.map((call, index) => {
      const functionName = signatureQueries[index]?.data?.functionName
      return {
        key: `call-${index}`,
        content: (
          <AddressDisplay
            address={data.smartAccountAddress}
            chainId={data.chainId}
            layout="two-line"
          />
        ),
        meta: (
          <div className="flex flex-col items-end justify-center gap-0.5">
            <span className="h-6 text-base leading-6 text-primary">
              {functionName
                ? `${functionName}()`
                : (selectors[index] ?? 'unknown()')}
            </span>
            {call.to && (
              <span className="h-4 text-sm leading-4 text-secondary">
                {shortenAddress(call.to)}
              </span>
            )}
          </div>
        ),
      }
    })
  }, [
    data.calls,
    data.smartAccountAddress,
    data.chainId,
    signatureQueries,
    selectors,
  ])

  const contractCallSection = (
    <ContractCallSection
      items={contractCallItems}
      decodedCalls={decodedCalls}
      isShowingCalls={isShowingCalls}
      secondaryDisplayButtons={
        <SecondaryDisplayButtons
          isShowingCalls={isShowingCalls}
          onToggleCalls={() => setIsShowingCalls(!isShowingCalls)}
          hasDecodedCalls={!!decodedCalls?.length}
          runTenderlySimulation={runTenderlySimulation}
          linkTarget="window"
        />
      }
    />
  )

  const title = buildTitle(signatureQueries, data.calls, data.source)

  return { contractCallSection, txnData, title }
}
